/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import VehicleMarker from "~/components/landing/bento/VehicleMarker";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const vehicleLocation = imageUrl("/images/landing/turo-vehicle-location.png");
const drivingHabitsWide = imageUrl("/images/landing/turo-driving-habits.png");
const vehicleHealth = imageUrl("/images/landing/turo-health-maintenance.png");
const monitorEverything = imageUrl("/images/landing/turo-monitor-everything.png");

/**
 * Ported from bouncieweb's `Bento/MiscGridRentals.js` (plus the `BentoGridH3` and
 * `gridGap` helpers from `Bento/BentoComponents.js`).
 *
 * A 4-item bento grid — "Vehicle Location", "Driving Habits", "Health &
 * Maintenance", "Monitor Everything" — that goes 1-up (with vw-tall rows) on
 * mobile, 2x2 from sm, and 4-across at xlg. Each tile pins its screenshot to the
 * bottom, deliberately bleeding past the tile edge, with pulsing `VehicleMarker`
 * pucks positioned on top at percentage offsets.
 *
 * Shared: /turo and /shoploaner both render this with no props, exactly as
 * bouncieweb's LandingTuro and LandingLoaner did.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   sm 640px -> sm:  md 768px -> md:  lg 1024px -> lg:  xlg 1280px -> xl:
 */

// GridItemCustom background: linear-gradient(blueLight1, blueLight8)
const tileGradient = `linear-gradient(${colors.blueLight1}, ${colors.blueLight8})`;

function Tile({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("relative overflow-hidden rounded-[20px]", className)}
      style={{ background: tileGradient }}
    >
      {children}
    </div>
  );
}

// H3Custom (BentoGridH3 override): centered, 24px, tightened leading.
function TileHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={cn(
        "font-display relative mx-auto my-6 w-full text-center text-[24px] leading-[1.2] font-extrabold",
        className,
      )}
    >
      {children}
    </h3>
  );
}

// StyledImage: full-width, natural height, no max-width clamp.
const styledImage = "h-fit w-full max-w-none p-0 m-0";

export default function MiscGridRentals() {
  return (
    // Grid
    <div
      className={cn(
        "mb-12 grid grid-cols-1 gap-6",
        "grid-rows-[340px_95vw_115vw_90vw]",
        "sm:mb-20 sm:grid-cols-2 sm:grid-rows-[repeat(2,20rem)]",
        "lg:grid-rows-[repeat(2,23rem)]",
        "xl:grid-cols-4 xl:grid-rows-[repeat(1,22rem)]",
      )}
    >
      {/* Vehicle Location */}
      <Tile>
        <TileHeading className="xl:w-[150px]">Vehicle Location</TileHeading>
        {/* VehicleLocationGraphicWrapper */}
        <div
          className={cn(
            "absolute bottom-0 left-1/2 -translate-x-1/2",
            "h-[270px] w-[270px] sm:h-[250px] sm:w-[250px] lg:h-[300px] lg:w-[300px] xl:h-[230px] xl:w-[230px]",
          )}
        >
          <VehicleMarker
            size="13%"
            styles={{
              position: "absolute",
              top: "29.75%",
              left: "57.5%",
              rotate: "90deg",
            }}
          />
          <VehicleMarker
            size="18%"
            styles={{
              position: "absolute",
              top: "73%",
              left: "6%",
              rotate: "90deg",
            }}
          />
          <Picture
            src={vehicleLocation}
            alt="A phone and notification demonstrating real-time location data"
            className={styledImage}
          />
        </div>
      </Tile>

      {/* Driving Habits */}
      <Tile>
        {/* DrivingHabitsGraphicWrapperWide */}
        <div
          className={cn(
            "absolute bottom-[-14%] left-0 h-[calc(100vw-2rem)] w-[calc(100vw-2rem)]",
            "sm:left-[-35px] sm:h-[320px] sm:w-[320px]",
            "md:bottom-[-50px] md:left-0 md:h-[320px] md:w-[320px]",
            "lg:bottom-[-150px] lg:h-[450px] lg:w-[450px]",
            "xl:bottom-[-45px] xl:left-[-40px] xl:h-[310px] xl:w-[310px]",
          )}
        >
          <VehicleMarker
            size="18%"
            delay="0.5s"
            styles={{ position: "absolute", top: "5%", left: "15.5%" }}
          />
          <Picture
            src={drivingHabitsWide}
            alt="A map showing driving events such as rapid acceleration, hard braking, and idle time"
            className={styledImage}
          />
        </div>
        <TileHeading className="xl:w-[150px]">Driving Habits</TileHeading>
      </Tile>

      {/* Health & Maintenance */}
      <Tile>
        <TileHeading>Health &amp; Maintenance</TileHeading>
        {/* HealthImage */}
        <Picture
          src={vehicleHealth}
          // Original alt began "An image of a mechanic..." — the leading
          // "An image of" is dropped because screen readers already announce
          // the element as an image (jsx-a11y/img-redundant-alt).
          alt="A mechanic working under a car, behind vehicle status showing the status of fuel level, battery level, and engine health"
          className={cn(
            styledImage,
            "absolute right-0 bottom-0 w-[102%]",
            "sm:bottom-[-30px] md:bottom-[-25px] md:w-[85%] lg:bottom-[-75px] xl:bottom-0 xl:w-[102%]",
          )}
        />
      </Tile>

      {/* Monitor Everything */}
      <Tile>
        {/* MonitorEverythingGraphicWrapper */}
        <div
          className={cn(
            "absolute bottom-[-72px] left-0",
            "h-[calc(100vw-2rem+2px)] w-[calc(100vw-2rem+2px)]",
            "sm:bottom-[-49px] sm:left-[-23px] sm:h-[280px] sm:w-[280px]",
            "md:bottom-[-75px] md:left-[-8px] md:h-[330px] md:w-[330px]",
            "lg:bottom-[-130px] lg:left-0 lg:h-[450px] lg:w-[450px]",
            "xl:bottom-[-47px] xl:left-[-30px] xl:h-[284px] xl:w-[284px]",
          )}
        >
          <VehicleMarker
            size="18%"
            delay="1s"
            styles={{
              position: "absolute",
              top: "25%",
              left: "12%",
              rotate: "45deg",
            }}
          />
          <VehicleMarker
            size="18%"
            delay="1.5s"
            color={colors.blue}
            styles={{
              position: "absolute",
              top: "57%",
              left: "44.25%",
              rotate: "135deg",
            }}
          />
          <VehicleMarker
            size="18%"
            delay="2s"
            color={colors.blue}
            styles={{
              position: "absolute",
              top: "20%",
              left: "73%",
              rotate: "-135deg",
            }}
          />
          <Picture
            src={monitorEverything}
            alt="A map showing the location of 3 vehicles simultaneously"
            className={styledImage}
          />
        </div>
        <TileHeading>Monitor Everything</TileHeading>
      </Tile>
    </div>
  );
}
